import { useContext, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import { XMarkIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { ScaleLoader } from "react-spinners";
import { PDFDownloadLink } from "@react-pdf/renderer";

import { useCalculateRoiMutation } from "@/graphql/generated/schema";
import { LeftArrow } from "@/icons/leftArrow";
import AppContext from "@/context/appContext";
import { hasRequiredRoiInput } from "@/lib/reportInput";
import CalculationExplanation from "@/components/calculationExplanation";
import PieChart from "@/components/pieChart";
import Divider from "@/components/divider";
import Button from "@/components/button";
import Modal from "@/components/modal";
import { ExternalLink } from "@/icons/externalLink";
import DownloadIcon from "@/icons/download";
import PdfDownload from "@/components/pdf/pdfDownload";
import Container from "@/components/container";
import "react-toastify/dist/ReactToastify.css";

const pic1 = "/images/icon_document.png";
const pic2 = "/images/icon_people.png";
const pic3 = "/images/icon_environment.png";

export default function ReportPage() {
  const { inputData } = useContext(AppContext);
  // RW-60: form state now lives in sessionStorage, so /report can be reached (deep-link, new tab)
  // with no/partial data. Guard it — redirect to the form rather than firing calculateRoi with
  // undefined fields (which previously rendered a silent dead page).
  const sufficient = hasRequiredRoiInput(inputData);
  const { t } = useTranslation("report");
  const navigate = useNavigate();
  const location = useLocation();
  const captchaToken =
    (location.state as { captchaToken?: string } | null)?.captchaToken ?? null;
  const [isData, setIsData] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chartImage, setChartImage] = useState<string | null>(null);

  const [calculateRoi, { data: calculationData, loading }] =
    useCalculateRoiMutation();

  const savingsText = "text-shadow inline-block rounded-md py-2 px-3";

  const calculateResults = async () => {
    if (!inputData) return;
    try {
      await calculateRoi({
        variables: {
          input: {
            captchaToken,
            majorProjectData: {
              duration: inputData.majorAverageDuration!,
              numEvaluators: inputData.majorNumberOfEvaluators!,
              contractValue: inputData.majorAverageContractValue!,
              numProcurements: inputData.majorNumberOfProcurements!,
            },
            strategicProjectData: {
              duration: inputData.strategicAverageDuration!,
              numEvaluators: inputData.strategicNumberOfEvaluators!,
              contractValue: inputData.strategicAverageContractValue!,
              numProcurements: inputData.strategicNumberOfProcurements!,
            },
            contactDetailsData: {
              firstName: inputData.firstName!,
              lastName: inputData.lastName!,
              emailAddress: inputData.email!,
              organisation: inputData.organisation!,
            },
          },
        },
      });
      setIsData(true);
    } catch (error) {
      console.error("calculateRoi failed", error);
      toast.error(t("calculation_error"), { toastId: "calculation_error" });
    }
  };

  useEffect(() => {
    if (sufficient) calculateResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No usable input (deep-link / new tab): send the user back to the form with a privacy-positive
  // notice instead of attempting a calculation. The form (MainForm) reads this router state.
  if (!sufficient) {
    return <Navigate to="/" replace state={{ privacyRedirect: true }} />;
  }

  if (loading)
    return (
      <div className="my-48 flex justify-center">
        <ScaleLoader color="#5a0377" height={50} radius={6} width={6} />
      </div>
    );

  if (isData) {
    const {
      annualProcessSavings,
      annualReductionsPaper,
      annualReductionsTreesSaved,
      annualRiskAvoidance,
      reducedClaims,
      reducedDelays,
      reducedRisk,
      reductionInChanges,
      reductionsForConsultancy,
      totalCostReductions,
      totalEfficiencySavings,
      totalSavings,
      costOfPetrol,
      environmentalSaving,
    } = calculationData?.calculateRoi ?? {};

    const calculatePercentages = (
      processSaving: number,
      riskSaving: number,
      environmentalSavingValue: number,
    ) => {
      const total = processSaving + riskSaving + environmentalSavingValue;
      const riskPercentage = Math.round((riskSaving / total) * 100);
      const processPercentage = Math.round((processSaving / total) * 100);
      const environmentalPercentage = Math.round(
        (environmentalSavingValue / total) * 100,
      );
      return { processPercentage, riskPercentage, environmentalPercentage };
    };

    const { riskPercentage, processPercentage, environmentalPercentage } =
      calculatePercentages(
        annualProcessSavings!,
        annualRiskAvoidance!,
        environmentalSaving!,
      );

    return (
      <>
        <div className="m-auto sm:mt-8 sm:w-5xl lg:w-full lg:max-w-440">
          <div className="bg-primary-bg pt-5 text-white">
            <h2
              className="mb-6 px-2 text-center text-xl leading-loose underline decoration-hot-pink decoration-4 underline-offset-8"
              data-testid="total-savings"
            >
              {t("total_estimate_heading", {
                totalSavings,
                maximumSignificantDigits: 9,
              })}
            </h2>
            <p className="px-3 text-center text-3xl leading-tight">
              {t("breakdown")}
            </p>
            <div className="lg:mt-2 lg:flex lg:items-center lg:justify-center lg:gap-x-10">
              <PieChart
                riskSaving={annualRiskAvoidance!}
                processSaving={annualProcessSavings!}
                environmentalSaving={environmentalSaving!}
                setChartImage={setChartImage}
              />

              <div className="flex flex-col items-center gap-4 px-8 py-2 text-lg lg:gap-y-5">
                <p className={classNames(savingsText, "bg-risk-savings")}>
                  {t("graph.risk_savings")} {`${riskPercentage}%`}
                </p>
                <p className={classNames(savingsText, "bg-hot-pink")}>
                  {t("graph.process_savings")} {`${processPercentage}%`}
                </p>
                <p
                  className={classNames(
                    savingsText,
                    "bg-environmental-savings",
                  )}
                >
                  {t("graph.environmental_savings")}{" "}
                  {`${environmentalPercentage}%`}
                </p>
              </div>
            </div>

            <div className="mx-6 mt-5 bg-white px-7 py-5 text-left text-black sm:mx-10 lg:pt-1 [&_p]:mb-1 [&_p]:text-md [&_p]:leading-relaxed [&_p]:tracking-wider">
              <h3 className="mb-5 text-center text-xl font-semibold lg:hidden">
                {t("mobile_heading")}
              </h3>
              <div className="hidden lg:block">
                <Divider text="Expected annual savings/reductions" />
              </div>

              <div className="lg:grid lg:grid-cols-3 lg:[&>div]:px-5">
                <div>
                  <div className="mb-2 mt-8 flex text-lg lg:mt-4">
                    <img src={pic2} width={80} alt="" />
                    <h4
                      className="ml-2 mt-3 font-semibold"
                      data-testid="risk-savings-total"
                    >
                      {t("section_2.total", {
                        annualRiskAvoidance,
                        maximumSignificantDigits: 9,
                      })}
                    </h4>
                  </div>
                  <p>
                    {t("section_2.contract_changes", {
                      reductionInChanges,
                      maximumSignificantDigits: 9,
                    })}
                  </p>
                  <p>
                    {t("section_2.wrong_supplier", {
                      reducedRisk,
                      maximumSignificantDigits: 9,
                    })}
                  </p>
                  <p>
                    {t("section_2.challenges", {
                      reducedDelays,
                      maximumSignificantDigits: 9,
                    })}
                  </p>
                  <p>
                    {t("section_2.claims", {
                      reducedClaims,
                      maximumSignificantDigits: 9,
                    })}
                  </p>
                </div>

                <div className="mx-auto mt-8 w-[95%] border-t-2 border-cd-purple lg:hidden" />

                <div className="lg:bg-light-gray lg:pb-4">
                  <div className="mb-2 mt-6 flex text-lg lg:mt-4">
                    <img src={pic1} width={80} alt="" />
                    <h4
                      className="ml-2 mt-3 font-semibold"
                      data-testid="process-savings-total"
                    >
                      {t("section_1.total", {
                        annualProcessSavings,
                        maximumSignificantDigits: 9,
                      })}
                    </h4>
                  </div>
                  <p>
                    {t("section_1.process_efficiency", {
                      totalEfficiencySavings,
                      maximumSignificantDigits: 9,
                    })}
                  </p>
                  <p>
                    {t("section_1.direct_cost", {
                      totalCostReductions,
                      maximumSignificantDigits: 9,
                    })}
                  </p>
                  <p>
                    {t("section_1.consultancy_reductions", {
                      reductionsForConsultancy,
                      maximumSignificantDigits: 9,
                    })}
                  </p>
                </div>

                <div className="mx-auto mt-8 w-[95%] border-t-2 border-cd-purple lg:hidden" />

                <div>
                  <div className="mb-2 mt-6 flex text-lg lg:mt-4">
                    <img src={pic3} width={80} alt="" />
                    <h4
                      className="ml-2 mt-3 font-semibold"
                      data-testid="environmental-savings-total"
                    >
                      {t("section_3.total", {
                        environmentalSaving,
                        maximumSignificantDigits: 9,
                      })}
                    </h4>
                  </div>
                  <p>
                    {t("section_3.paper", {
                      annualReductionsPaper,
                      maximumSignificantDigits: 9,
                    })}
                  </p>
                  <p>
                    {t("section_3.petrol", {
                      costOfPetrol,
                      maximumSignificantDigits: 9,
                    })}
                  </p>
                  <p>
                    {t("section_3.trees", {
                      annualReductionsTreesSaved,
                      maximumSignificantDigits: 9,
                    })}
                  </p>
                </div>
              </div>

              <div className="lg: mb-4 mt-8 grid gap-x-6 gap-y-5 uppercase lg:mx-auto lg:w-[60%] lg:grid-cols-2">
                <Button
                  variant="secondary"
                  onClick={() => setIsModalOpen(true)}
                  className="uppercase"
                >
                  {t("calculation_explanation")}
                </Button>
                <Button
                  className="inline-flex items-center justify-center pr-5 uppercase md:ml-0"
                  onClick={() => navigate(-1)}
                >
                  <LeftArrow className="mr-2 mt-0.5 stroke-current" />
                  {t("update_settings")}
                </Button>

                <div className="flex items-center justify-center gap-x-2 text-md font-medium underline">
                  <a
                    href="https://commercedecisions.com/resource/return-on-investment/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ROI Datasheet
                  </a>
                  <ExternalLink />
                </div>
                <PDFDownloadLink
                  document={
                    <PdfDownload
                      totalSavings={totalSavings!}
                      totalRisk={annualRiskAvoidance!}
                      contractChanges={reductionInChanges!}
                      wrongSupplier={reducedRisk!}
                      potentialDelays={reducedDelays!}
                      potentialClaims={reducedClaims!}
                      processTotal={annualProcessSavings!}
                      processEfficiency={totalEfficiencySavings!}
                      directCosts={totalCostReductions!}
                      externalConsultancy={reductionsForConsultancy!}
                      environmentalExpected={environmentalSaving!}
                      paperSavings={annualReductionsPaper!}
                      petrolCosts={costOfPetrol!}
                      treesSaved={annualReductionsTreesSaved!}
                      chartImage={chartImage}
                      riskPercentage={riskPercentage}
                      processPercentage={processPercentage}
                      environmentalPercentage={environmentalPercentage}
                    />
                  }
                  fileName="ROI Report"
                >
                  <Button
                    className="flex w-full items-center justify-center gap-x-2 text-md font-medium uppercase"
                    variant="secondary"
                    data-testid="pdf-download"
                  >
                    {t("download")} <DownloadIcon />
                  </Button>
                </PDFDownloadLink>
              </div>
            </div>
          </div>
        </div>

        <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
          <div className="mb-2 flex justify-end">
            <XMarkIcon
              onClick={() => setIsModalOpen(false)}
              className="h-8 w-8 cursor-pointer"
            />
          </div>
          <CalculationExplanation />
        </Modal>
      </>
    );
  }

  return (
    <Container>
      <ToastContainer position="top-center" theme="colored" />
      <Button className="inline-flex" onClick={() => navigate(-1)}>
        <LeftArrow className="mr-2 stroke-current" />
        {t("back_button")}
      </Button>
    </Container>
  );
}
